import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from './Button';
import { getCreditBalance, spendCredits } from '../services/creditsService';
import { useAuth } from '../contexts/AuthContext';

interface CreditPurchaseButtonProps {
  price: number;
  productName?: string;
  sellerId?: string;
  showCreditsBalance?: boolean;
  showBuyButton?: boolean;
  className?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const CreditPurchaseButton: React.FC<CreditPurchaseButtonProps> = ({
  price,
  productName,
  sellerId,
  showCreditsBalance = false,
  showBuyButton = false,
  className = '',
  onSuccess,
  onError
}) => {
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Only fetch credit balance if authenticated
    if (!isAuthenticated) {
      setCreditBalance(0);
      return;
    }

    // Get initial credit balance
    const fetchBalance = async () => {
      try {
        const balance = await getCreditBalance();
        setCreditBalance(balance);
      } catch (error) {
        console.error('Failed to fetch balance:', error);
        setCreditBalance(0);
      }
    };

    fetchBalance();

    // Set up interval to refresh credit balance
    const intervalId = setInterval(() => {
      if (isAuthenticated) {
        fetchBalance();
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isAuthenticated]); // Re-run when auth status changes

  const handlePurchase = async () => {
    if (creditBalance < price) {
      setError('Insufficient credits. Please add more credits to continue.');
      return;
    }

    setError(null);
    setIsProcessing(true);

    try {
      await spendCredits(price, productName, sellerId);

      // Update balance after purchase - fix for the Promise issue
      try {
        const newBalance = await getCreditBalance();
        setCreditBalance(newBalance);
      } catch (error) {
        console.error("Error fetching updated balance:", error);
      }

      setIsProcessing(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setIsProcessing(false);
      const error = err instanceof Error ? err : new Error('An unknown error occurred');
      setError(error.message);

      if (onError) {
        onError(error);
      }
    }
  };

  const handleTopUp = () => {
    navigate('/topup');
  };

  const hasEnoughCredits = creditBalance >= price;

  if (!showBuyButton && !showCreditsBalance) {
    return null;
  }

  return (
    <div className={className}>
      {showCreditsBalance && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="text-[#7F56D9] mr-2"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-sm text-[#A3A3A3]">Your balance: {creditBalance.toFixed(0)} Credits</span>
          </div>
          <button
            onClick={handleTopUp}
            className="text-sm text-[#7F56D9] hover:underline"
          >
            Top up
          </button>
        </div>
      )}

      {showBuyButton && (
        <>
          {hasEnoughCredits ? (
            <Button
              variant="primary"
              fullWidth
              onClick={handlePurchase}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : `Buy with ${price.toFixed(0)} Credits`}
            </Button>
          ) : (
            <Button
              variant="outline"
              fullWidth
              onClick={handleTopUp}
            >
              Top Up Credits to Purchase
            </Button>
          )}

          {error && (
            <p className="text-xs text-[#F04438] mt-1">
              {error} <Link to="/topup" className="underline">Add more credits</Link>
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default CreditPurchaseButton;
